import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, CheckCircle, Clock, Shield, Lightbulb, TrendingUp, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const TestaFilmprojekt = () => {
  const { language, setLanguage, content } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      <Header 
        language={language} 
        onLanguageChange={setLanguage} 
        content={content.header} 
      />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-block px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm font-medium text-primary mb-6">
            🎬 Testa innan du investerar
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Testa ditt filmprojekt <span className="text-primary">innan du satsar budgeten</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            AI + mänsklig expertfeedback + provklipp på stilen t.ex. med er logga integrerad
          </p>
          
          <div className="bg-muted/50 border border-border rounded-lg p-6 mb-12 max-w-2xl mx-auto">
            <p className="text-lg text-foreground">
              Film är en stor investering. Med vårt test kan du se om idén håller – <em>innan du lägger budgeten</em>.
            </p>
          </div>
          
          <Button size="lg" className="bg-gradient-primary text-white hover:opacity-90 text-lg px-8 py-4 h-auto mb-4">
            <Play className="mr-2 w-5 h-5" />
            Testa ditt filmprojekt gratis
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          
          <p className="text-sm text-muted-foreground">
            Obs. vi reserverar oss ett begränsat antal fria förfrågningar per månad.
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Så funkar det
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center p-6">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-primary">1</span>
                </div>
                <h3 className="font-semibold mb-3">Fyll i underlag</h3>
                <p className="text-sm text-muted-foreground">
                  Beskriv ditt filmprojekt: typ, längd, antal, format, stil och mål
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center p-6">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-primary">2</span>
                </div>
                <h3 className="font-semibold mb-3">AI-analys</h3>
                <p className="text-sm text-muted-foreground">
                  Volyms AI-film-bot ger automatiskt sina synpunkter och skickar till vår filmexpert
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center p-6">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-primary">3</span>
                </div>
                <h3 className="font-semibold mb-3">Expertfeedback</h3>
                <p className="text-sm text-muted-foreground">
                  Vår helt mänskliga filmexpert ser över projektet och ger sin feedback
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center p-6">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-primary">4</span>
                </div>
                <h3 className="font-semibold mb-3">Provklipp</h3>
                <p className="text-sm text-muted-foreground">
                  Få ett provklipp i vald stil – med din logga – för att se exempel innan budget
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Du får ut
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-lg mb-2">Trygghet i ditt beslut</h3>
                <p className="text-muted-foreground">
                  Få bekräftelse på att din filmidé fungerar innan du investerar din budget
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <CheckCircle className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-lg mb-2">Feedback från en filmexpert</h3>
                <p className="text-muted-foreground">
                  Professionell bedömning från våra erfarna filmexperter
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <Lightbulb className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-lg mb-2">Inspiration till stil & koncept</h3>
                <p className="text-muted-foreground">
                  Kreativa förslag och idéer för att förbättra ditt projekt
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <TrendingUp className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-lg mb-2">Ett konkret underlag för budgetbeslut</h3>
                <p className="text-muted-foreground">
                  Välgrundad information för att fatta rätt beslut om investering
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 px-4 bg-gradient-primary text-white">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            👉 Testa ditt projekt gratis idag
          </h2>
          
          <p className="text-xl mb-8 opacity-90">
            Få expertfeedback och provklipp innan du investerar din budget
          </p>
          
          <Button size="lg" variant="secondary" className="text-lg px-8 py-4 h-auto mb-4">
            <Play className="mr-2 w-5 h-5" />
            Testa ditt filmprojekt fritt
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          
          <p className="text-sm opacity-75">
            Obs. vi reserverar oss ett begränsat antal fria förfrågningar per månad.
          </p>
        </div>
      </section>

      <Footer content={content.footer} />
    </div>
  );
};

export default TestaFilmprojekt;